import { startGoogleSignIn } from './google-auth';
import { authClient } from '@/lib/better-auth-client-actions';
import { getAuthErrorCode } from '@/lib/auth-error-handler';
import { isInlineOnlyAuthError } from '@/lib/auth-inline-errors';
import { reduceCredentialRecoveryReason } from '@/lib/auth/credential-recovery-error';
import { isEmailNotVerifiedAuthError } from '@/lib/auth/email-not-verified';
import {
  clearPendingAuthPassword,
  persistPendingAuthPassword,
} from '@/lib/auth/pending-auth-password';
import { clearStoredReactivationEmail } from '@/lib/reactivation-state';
import { safeSessionStorageRemoveItem } from '@/lib/utils';
import { toast } from 'sonner';
import type { useSignInState } from './use-sign-in-state';
import type { SignInFormType } from './use-sign-in-state';
import { pushAuthHandoff } from '@/lib/auth/auth-handoff';
export function createSignInCredentialActions(
  state: ReturnType<typeof useSignInState>
) {
  const {
    inviteToken,
    authBasePath,
    isRecord,
    SELF_DEACTIVATED_ACCOUNT_ERROR_CODE,
    router,
    searchParams,
    locale,
    copy,
    handleAuthError,
    returnTo,
    setIsLoading,
    setError,
    setCredentialRecoveryReason,
    setPendingMethod,
    setPendingPassword,
    form,
    goToReactivateStep,
    applyFieldErrors,
  } = state;

  const handleSubmit = async (values: SignInFormType) => {
    setIsLoading(true);
    setPendingMethod('credentials');
    setError(null);
    setCredentialRecoveryReason((current) =>
      reduceCredentialRecoveryReason(current, { type: 'new-submit' })
    );
    form.clearErrors();

    let handedOff = false;
    try {
      // Store invitation token if present
      if (inviteToken) {
        localStorage.setItem('pendingInvitation', inviteToken);
      }

      // Normalize email to lowercase for case-insensitive handling
      const normalizedEmail = values.email.trim().toLowerCase();

      console.log('[auth:client] signIn start', {
        email: normalizedEmail,
        hasInvite: Boolean(inviteToken),
      });

      // Try email/password sign in with Better Auth
      // Save password to re-use after OTP verification if needed
      setPendingPassword(values.password);
      const result = await authClient.signIn.email({
        email: normalizedEmail,
        password: values.password,
      });

      if (result.error) {
        console.error('[auth:client] signIn error', { error: result.error });

        // Check if error is due to invalid origin - don't send OTP in this case
        const errorObj = result.error;
        const errorMessageText =
          isRecord(errorObj) && typeof errorObj.message === 'string'
            ? errorObj.message
            : '';
        const isOriginError =
          errorMessageText.toLowerCase().includes('invalid origin') ||
          errorMessageText.toLowerCase().includes('origin');

        if (isOriginError) {
          // Origin error - don't try to send OTP, just show the error
          const { error: errorMessage, fieldErrors: newFieldErrors } =
            handleAuthError(result.error);
          if (errorMessage) {
            setError(errorMessage || copy.authConfigError);
            toast.error(errorMessage || copy.authConfigError);
          }
          if (Object.keys(newFieldErrors).length > 0) {
            applyFieldErrors(newFieldErrors);
          }
          setIsLoading(false);
          return;
        }

        // Only advance to OTP when Better Auth confirms email-not-verified.
        // Wrong passwords are 401 and must stay on the credentials step.
        if (isEmailNotVerifiedAuthError(result.error)) {
          console.log('[auth:client] email not verified, sending OTP');

          try {
            persistPendingAuthPassword(normalizedEmail, values.password);
            setPendingPassword(values.password);

            await authClient.emailOtp.sendVerificationOtp({
              email: normalizedEmail,
              type: 'email-verification',
            });

            const params = new URLSearchParams(searchParams.toString());
            params.set('step', 'otp');
            params.set('email', normalizedEmail);
            router.push(`${authBasePath}?${params.toString()}`);

            toast.info(copy.pleaseVerifyEmail);
            return;
          } catch (otpError) {
            console.error(
              '[auth:client] failed to send verification OTP',
              otpError
            );
            clearPendingAuthPassword();
            // Fall through to regular error handling
          }
        }

        if (
          getAuthErrorCode(result.error) === SELF_DEACTIVATED_ACCOUNT_ERROR_CODE
        ) {
          goToReactivateStep(normalizedEmail);
          setIsLoading(false);
          return;
        }

        const { error: errorMessage, fieldErrors: newFieldErrors } =
          handleAuthError(result.error);

        if (errorMessage) {
          setError(errorMessage);
          if (!isInlineOnlyAuthError(result.error)) toast.error(errorMessage);
        }
        if (Object.keys(newFieldErrors).length > 0) {
          applyFieldErrors(newFieldErrors);
        }
        setIsLoading(false);
        return;
      }

      console.log(
        '[auth:client] signIn success',
        result.data as Record<string, unknown>
      );

      clearPendingAuthPassword();
      clearStoredReactivationEmail(safeSessionStorageRemoveItem);
      if (returnTo) {
        handedOff = pushAuthHandoff(
          router,
          `/auth/signing-in?returnTo=${encodeURIComponent(returnTo)}`
        );
      } else {
        handedOff = pushAuthHandoff(router, '/auth/signing-in');
      }
    } catch (error) {
      if (getAuthErrorCode(error) === SELF_DEACTIVATED_ACCOUNT_ERROR_CODE) {
        const normalizedEmail = values.email.trim().toLowerCase();
        goToReactivateStep(normalizedEmail);
        return;
      }

      console.error('[auth:client] signIn exception', { error });
      const { error: errorMessage, fieldErrors: newFieldErrors } =
        handleAuthError(error);

      if (errorMessage) {
        setError(errorMessage);
        toast.error(errorMessage);
      }

      if (Object.keys(newFieldErrors).length > 0) {
        applyFieldErrors(newFieldErrors);
        Object.values(newFieldErrors).forEach((msg) => toast.error(msg));
      }
    } finally {
      // A handoff keeps the button pending until the page unmounts (#5559).
      if (!handedOff) {
        setIsLoading(false);
        setPendingMethod(null);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setPendingMethod('google');
    setError(null);
    form.clearErrors();

    try {
      // Store invitation token if present
      if (inviteToken) {
        localStorage.setItem('pendingInvitation', inviteToken);
      }

      const callbackURL = returnTo
        ? `/auth/signing-in?returnTo=${encodeURIComponent(returnTo)}`
        : '/auth/signing-in';
      const error = await startGoogleSignIn({ returnTo, locale, callbackURL });

      if (error) {
        const { error: errorMessage } = handleAuthError(error);
        setError(errorMessage || copy.googleSignInFailed);
        toast.error(errorMessage || copy.googleSignInFailed);
      }
    } catch (error) {
      const { error: errorMessage } = handleAuthError(error);
      setError(errorMessage || copy.googleSignInFailed);
    } finally {
      setIsLoading(false);
      setPendingMethod(null);
    }
  };
  return { handleSubmit, handleGoogleSignIn };
}
